import { API_URL } from "../config";

const TOKEN_STORAGE_KEY = "authToken";

// Backend rotates the JWT when 5 minutes or less remain in its lifetime.
// Admin `refresh_threshold_seconds` is the backend counterpart of this window.
export const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

// How long a stale-token 401 waits for an in-flight sibling response to
// deliver the rotated token before falling back to a forced refresh.
const ROTATION_WAIT_MS = 3000;

// Lightweight authenticated endpoint used to force a token rotation.
const REFRESH_PROBE_URL = `${API_URL}/profile`;

const tokenRotationSubscribers = new Set();
let ensureFreshPromise = null;

export const getStoredAuthToken = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const subscribeToTokenRotation = (handler) => {
  tokenRotationSubscribers.add(handler);
  return () => tokenRotationSubscribers.delete(handler);
};

// Called whenever a response delivers a rotated token so any request that is
// currently waiting on a stale-token 401 can replay immediately.
export const notifyAuthTokenRotated = (newToken) => {
  if (!newToken) {
    return;
  }

  tokenRotationSubscribers.forEach((handler) => {
    try {
      handler(newToken);
    } catch (e) {}
  });
};

const decodeBase64UrlSegment = (segment) => {
  try {
    const base64 = String(segment).replace(/-/g, "+").replace(/_/g, "/");
    const padding =
      base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
    const padded = base64 + padding;

    const raw =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("binary");

    const percentEncoded = Array.prototype.map
      .call(raw, (char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join("");

    return decodeURIComponent(percentEncoded);
  } catch (e) {
    return null;
  }
};

export const getTokenExpiryMs = (token) => {
  if (!token || typeof token !== "string") {
    return null;
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }

  const json = decodeBase64UrlSegment(parts[1]);
  if (!json) {
    return null;
  }

  try {
    const payload = JSON.parse(json);
    if (payload && typeof payload.exp === "number") {
      return payload.exp * 1000;
    }
  } catch (e) {}

  return null;
};

// Returns true only when we can positively determine the token expires within
// the threshold. Unknown expiry returns false so we never block requests on a
// token we cannot read.
export const isTokenExpiringSoon = (
  token,
  threshold = TOKEN_REFRESH_THRESHOLD_MS,
) => {
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs == null) {
    return false;
  }

  return expiryMs - Date.now() <= threshold;
};

// Resolves with a rotated token once the stored token differs from
// `staleToken`, or `null` if no rotation is observed within `timeoutMs`.
export const waitForRotatedToken = (staleToken, timeoutMs = ROTATION_WAIT_MS) =>
  new Promise((resolve) => {
    const current = getStoredAuthToken();
    if (current && current !== staleToken) {
      resolve(current);
      return;
    }

    let settled = false;
    let unsubscribe = () => {};
    let timer = null;

    const finish = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe();
      if (timer) {
        clearTimeout(timer);
      }
      resolve(value);
    };

    unsubscribe = subscribeToTokenRotation((newToken) => {
      if (newToken && newToken !== staleToken) {
        finish(newToken);
      }
    });

    timer = setTimeout(() => {
      const latest = getStoredAuthToken();
      finish(latest && latest !== staleToken ? latest : null);
    }, timeoutMs);
  });

const runTokenRefreshProbe = async (instance, staleToken) => {
  try {
    await instance.get(REFRESH_PROBE_URL, {
      _tokenRefreshProbe: true,
    });
  } catch (e) {
    // A failing probe means the token could not be refreshed.
  }

  const latest = getStoredAuthToken();
  return latest && latest !== staleToken ? latest : null;
};

// Single-flight token refresh. Concurrent callers share one in-flight probe.
export const ensureFreshToken = (
  instance,
  { force = false, threshold = TOKEN_REFRESH_THRESHOLD_MS } = {},
) => {
  const token = getStoredAuthToken();

  if (!token) {
    return Promise.resolve(null);
  }

  if (!force && !isTokenExpiringSoon(token, threshold)) {
    return Promise.resolve(token);
  }

  if (ensureFreshPromise) {
    return ensureFreshPromise;
  }

  ensureFreshPromise = runTokenRefreshProbe(instance, token).finally(() => {
    ensureFreshPromise = null;
  });

  return ensureFreshPromise;
};
